import { getEnv } from './env';

// Runtime-safe fallback IDs to avoid local build env precedence (.env.local) breaking prod OAuth.
const PROD_GOOGLE_CLIENT_ID = '309490957520-mogkv03c5p4f9rro5dr3rsctj6umgeug.apps.googleusercontent.com';
const DEV_GOOGLE_CLIENT_ID = '309490957520-gtcpp21o6fcpa953g1a5a41iqa4r1bdo.apps.googleusercontent.com';

export const isLikelyJwt = (token) => typeof token === 'string' && token.split('.').length === 3;

const decodeBase64Url = (value) => {
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
  if (typeof window !== 'undefined' && window.atob) {
    return window.atob(padded);
  }
  return '';
};

export const parseJwtPayload = (token) => {
  if (!isLikelyJwt(token)) return null;
  try {
    return JSON.parse(decodeBase64Url(token.split('.')[1]));
  } catch (e) {
    return null;
  }
};

export const getExpectedGoogleClientId = () => {
  const configured = getEnv('REACT_APP_GOOGLE_CLIENT_ID', '');
  if (typeof window === 'undefined') {
    return configured || PROD_GOOGLE_CLIENT_ID;
  }

  const host = window.location.hostname;
  const isProdHost = host === 'originselect.com' || host === 'www.originselect.com';

  if (isProdHost) {
    return PROD_GOOGLE_CLIENT_ID;
  }

  if (host === 'localhost' || host === '127.0.0.1') {
    return configured || DEV_GOOGLE_CLIENT_ID;
  }

  return configured || PROD_GOOGLE_CLIENT_ID;
};

export const isUnsupportedGoogleDevOrigin = () => {
  if (typeof window === 'undefined') return false;
  return process.env.NODE_ENV === 'development' && (
    window.location.hostname === '127.0.0.1' ||
    window.location.hostname === 'localhost'
  );
};

const GSI_BLOCKED_ORIGIN_KEY = 'gsiBlockedOrigin';

export const markGsiOriginBlocked = () => {
  if (typeof window === 'undefined' || !window.sessionStorage) return;
  const currentOrigin = window.location.origin;
  const clientId = getExpectedGoogleClientId();
  sessionStorage.setItem(
    GSI_BLOCKED_ORIGIN_KEY,
    JSON.stringify({
      origin: currentOrigin,
      clientId,
      blockedAt: Date.now(),
    })
  );
};

export const isCurrentOriginMarkedGsiBlocked = () => {
  if (typeof window === 'undefined' || !window.sessionStorage) return false;

  const raw = sessionStorage.getItem(GSI_BLOCKED_ORIGIN_KEY);
  if (!raw) return false;

  try {
    const parsed = JSON.parse(raw);
    const currentOrigin = window.location.origin;
    const clientId = getExpectedGoogleClientId();
    return parsed?.origin === currentOrigin && parsed?.clientId === clientId;
  } catch (e) {
    sessionStorage.removeItem(GSI_BLOCKED_ORIGIN_KEY);
    return false;
  }
};

export const tokenMatchesExpectedGoogleClientId = (token) => {
  const expectedClientId = getExpectedGoogleClientId();
  if (!expectedClientId) return true;

  const payload = parseJwtPayload(token);
  if (!payload?.aud) return false;

  if (Array.isArray(payload.aud)) {
    return payload.aud.includes(expectedClientId);
  }

  return payload.aud === expectedClientId;
};

export const isUsableGoogleIdToken = (token) => {
  if (!isLikelyJwt(token)) return false;
  if (!tokenMatchesExpectedGoogleClientId(token)) return false;
  const payload = parseJwtPayload(token);
  if (!payload) return false;
  if (!payload.exp) return true;
  const now = Math.floor(Date.now() / 1000);
  return payload.exp > now + 30;
};

export const getStoredGoogleIdToken = () => {
  if (typeof window === 'undefined' || !window.localStorage) return null;
  const dedicatedToken = localStorage.getItem('userIdToken');
  if (dedicatedToken && !isUsableGoogleIdToken(dedicatedToken)) {
    localStorage.removeItem('userIdToken');
  }
  if (isUsableGoogleIdToken(dedicatedToken)) return dedicatedToken;

  const legacyToken = localStorage.getItem('userToken');
  if (legacyToken && !isUsableGoogleIdToken(legacyToken)) {
    localStorage.removeItem('userToken');
    return null;
  }
  if (isUsableGoogleIdToken(legacyToken)) {
    localStorage.setItem('userIdToken', legacyToken);
    return legacyToken;
  }

  return null;
};

export const hasStoredGoogleIdToken = () => Boolean(getStoredGoogleIdToken());

export const clearStoredGoogleIdToken = ({ force = false } = {}) => {
  if (typeof window === 'undefined' || !window.localStorage) return;
  localStorage.removeItem('userIdToken');
  const legacyToken = localStorage.getItem('userToken');
  if (force || (legacyToken && !isUsableGoogleIdToken(legacyToken))) {
    localStorage.removeItem('userToken');
  }
};

export const clearStoredGoogleSession = () => {
  if (typeof window === 'undefined' || !window.localStorage) return;
  clearStoredGoogleIdToken({ force: true });
  localStorage.removeItem('googleAccessToken');
  localStorage.removeItem('user');
  localStorage.removeItem('userData');
  localStorage.removeItem('userSignInMethod');
  localStorage.removeItem('userSignInTime');
  localStorage.removeItem('userLastActivity');
};